import type { ComponentPropsWithoutRef, HTMLAttributes } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Icon } from '@heyroot/ui/primitives/Icon';
import { cn } from './cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-[calc(var(--z-modal)+2)]',
        'animate-overlay-scrim-in',
        className,
      )}
      style={{
        background: 'var(--overlay-scrim)',
        backdropFilter: 'blur(var(--scrim-blur))',
        WebkitBackdropFilter: 'blur(var(--scrim-blur))',
      }}
      {...props}
    />
  );
}

export type DialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Corner dismiss button. On by default for Dialog-composed surfaces. Modal
   * opts out: its pre-Radix implementation had no corner X, and several of its
   * call sites render their own header dismiss control, which would otherwise
   * sit next to a second button with the same "Close" accessible name.
   */
  showCloseButton?: boolean;
};

export function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        style={{ boxShadow: 'var(--shadow-dialog)' }}
        className={cn(
          'fixed left-1/2 top-1/2 z-[calc(var(--z-modal)+2)] w-full max-w-lg',
          '-translate-x-1/2 -translate-y-1/2',
          'rounded-card bg-surface-elevated p-8',
          'animate-overlay-fade-in',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close
            style={{ position: 'absolute', top: '1.5rem', right: '1.5rem' }}
            className="rounded-control text-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring-focus"
          >
            <Icon as={X} size="sm" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex justify-end gap-2 pt-6', className)} {...props} />;
}

export function DialogTitle({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title className={cn('text-heading-3 text-primary', className)} {...props} />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-body-sm text-secondary', className)}
      {...props}
    />
  );
}
